import { useCallback, useEffect, useRef, useState } from 'react'
import { useSocket } from '../contexts/SocketContext'
import { useNotifications } from '../contexts/NotificationContext'

/**
 * CO-BROWSE (Phase 1, agent side) — manual, view-only, consented.
 *
 * The agent presses "Start co-browse"; this hook asks the visitor for consent
 * over the socket and tracks the session state. Nothing is viewed until the
 * visitor accepts. The actual live mirror is rendered by <CobrowseViewer/> when
 * state === 'active'. See packages/api/src/services/cobrowse.service.ts.
 */
export type CobrowseState = 'idle' | 'requesting' | 'active'

export function useCobrowse(chatId: string) {
  const { socket } = useSocket()
  const { addNotification } = useNotifications()
  const [state, setState] = useState<CobrowseState>('idle')
  const stateRef = useRef<CobrowseState>('idle')
  stateRef.current = state

  useEffect(() => {
    if (!socket) return

    const onRequested = (d: any) => {
      if (String(d?.chatId) !== String(chatId)) return
      setState('requesting')
    }
    const onStarted = (d: any) => {
      if (String(d?.chatId) !== String(chatId)) return
      setState('active')
    }
    const onDeclined = (d: any) => {
      if (String(d?.chatId) !== String(chatId)) return
      setState('idle')
      addNotification('The visitor declined the screen-sharing request.', 'warning')
    }
    const onTimeout = (d: any) => {
      if (String(d?.chatId) !== String(chatId)) return
      setState('idle')
      addNotification('Screen-sharing request timed out — no response from the visitor.', 'warning')
    }
    const onStopped = (d: any) => {
      if (String(d?.chatId) !== String(chatId)) return
      const wasActive = stateRef.current === 'active'
      setState('idle')
      if (d?.reason === 'visitor_disconnect') {
        addNotification('Co-browse ended — the visitor left the page.', 'info')
      } else if (d?.reason === 'rate') {
        addNotification('Co-browse ended automatically (data-rate limit).', 'warning')
      } else if (wasActive && d?.reason !== 'manual') {
        addNotification('Co-browse ended.', 'info')
      }
    }
    const onError = (d: any) => {
      if (String(d?.chatId) !== String(chatId)) return
      setState('idle')
      const reason = d?.reason
      addNotification(
        reason === 'capacity'
          ? 'Too many co-browse sessions are active right now — try again shortly.'
          : reason === 'in_progress'
          ? 'A co-browse session is already running for this chat.'
          : 'Could not start co-browse.',
        'error',
      )
    }

    socket.on('cobrowse_requested', onRequested)
    socket.on('cobrowse_started', onStarted)
    socket.on('cobrowse_declined', onDeclined)
    socket.on('cobrowse_timeout', onTimeout)
    socket.on('cobrowse_stopped', onStopped)
    socket.on('cobrowse_error', onError)

    return () => {
      socket.off('cobrowse_requested', onRequested)
      socket.off('cobrowse_started', onStarted)
      socket.off('cobrowse_declined', onDeclined)
      socket.off('cobrowse_timeout', onTimeout)
      socket.off('cobrowse_stopped', onStopped)
      socket.off('cobrowse_error', onError)
    }
  }, [socket, chatId, addNotification])

  // End the session if the agent navigates away mid-session.
  useEffect(() => {
    return () => {
      if (socket && stateRef.current !== 'idle') {
        socket.emit('cobrowse_stop', { chatId: String(chatId) })
        socket.emit('cobrowse_leave', { chatId: String(chatId) })
      }
    }
  }, [socket, chatId])

  const start = useCallback(() => {
    if (!socket) {
      addNotification('Not connected — cannot start co-browse.', 'error')
      return
    }
    socket.emit('cobrowse_join', { chatId: String(chatId), role: 'agent' })
    socket.emit('cobrowse_request', { chatId: String(chatId) })
    setState('requesting')
  }, [socket, chatId, addNotification])

  const stop = useCallback(() => {
    if (socket) socket.emit('cobrowse_stop', { chatId: String(chatId) })
    setState('idle')
  }, [socket, chatId])

  return { state, start, stop }
}
